import React, { useState } from "react"
import styled from "styled-components"
import { FormGroup, FormActions } from "@src/components/form"
import Button from "@src/components/core-button"
import RouteReviewsService from "@src/services/reviews/route-reviews"
import { useFormContext } from "react-hook-form"
import GTM from "@src/services/gtm"
import InputStars from "../reusable/forms/input-stars"
import { required } from "@src/utils/validators"
import InputSymbols from "../reusable/forms/input-symbols"
import InputArea from "../reusable/forms/input-area"
import FormInput from "../reusable/forms/form-input"

const FormStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const SlidersHolder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
`

const ReviewDetails = styled.div`
  font-weight: 500;
  & > label {
    display: inline-block;
    font-weight: normal;
    font-style: italic;
    font-size: 0.8em;
  }

  .field-header {
    font-style: normal;
    display: block;
    font-size: 18px;
    font-weight: 700;
    line-height: 32px;
    color: #667085;
  }
`

const LengthFromGroup = styled(FormGroup)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  gap: 0;
  div {
    width: calc(50% - 14px);
  }
`

const StyledFormActions = styled(FormActions)`
  display: flex;
  justify-content: center;
  align-items: center;

  .form-success {
    margin-top: 0;
  }
  .form-error {
    margin-top: 0;
  }
  .form-info {
    margin-top: 0;
  }

  & button {
    width: 100vw;
    padding: 16px 28px;
    gap: 8px;
    flex-shrink: 0;
    border-radius: 8px;
    background: #2b4346;

    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  }
`

function FormReview({ routeId, onReviewAdded }) {
  const [formStatus, setFormStatus] = useState(null)
  const { handleSubmit } = useFormContext()

  const submit = async data => {
    let name = "route-review"

    try {
      const submitData = data
      const result = {}

      result.submit = await handleFormSubmit(submitData)

      const dataLayer = {
        event: "10a.form.success",
      }
      if (name) {
        dataLayer["form-name"] = name
      }
      GTM.dataLayerPush(dataLayer)
    } catch (error) {
      console.error(error)
    }
  }

  const handleFormSubmit = async values => {
    setFormStatus({
      processing: "Thanks! Just one moment while we post your review...",
    })

    try {
      await RouteReviewsService.createRouteReview(routeId, values)

      setFormStatus({
        success: "Thanks for the review!",
      })
      if (onReviewAdded) {
        onReviewAdded()
      }
    } catch (error) {
      setFormStatus({
        error: "There was an error posting your review.",
      })
    }
  }

  return (
    <FormStyle>
      <SlidersHolder>
        <InputStars
          label="Overall Rating"
          name="ratings[overall]"
          max={10}
          validate={required("Rating is required.")}
        />

        <InputSymbols
          label="Technical Difficulty"
          name="ratings[technical]"
          validate={required("Technical Difficulty is required.")}
        />

        <InputSymbols
          label="Physical Difficulty"
          name="ratings[physical]"
          validate={required("Physical Difficulty is required.")}
        />
      </SlidersHolder>

      <ReviewDetails>
        <label className="field-header">Title</label>

        <FormInput
          name="review-title"
          placeholder="Enter a tagline for your adventure!"
          validate={required("The tagline field is required.")}
        />
      </ReviewDetails>

      <ReviewDetails>
        <label className="field-header">Trip Length</label>
        <LengthFromGroup>
          <FormInput
            name="time-taken[hours]"
            placeholder="hours"
            min={0}
            type="number"
          />

          <FormInput
            name="time-taken[minutes]"
            placeholder="minutes"
            type="number"
            min={0}
            max={59}
          />
        </LengthFromGroup>
      </ReviewDetails>

      <ReviewDetails>
        <label className="field-header">Description</label>
        <InputArea
          name="review-content"
          placeholder={`Share your experience with fellow adventurers...
          How were the trail conditions?
          What were some highlights?
          What should people know about this trip?`}
        />
      </ReviewDetails>
      <br />
      <StyledFormActions
        className="horizontal"
        submitting={formStatus && formStatus.processing ? true : undefined}
      >
        {(formStatus?.success && (
          <p className="form-success">{formStatus.success}</p>
        )) ||
          (formStatus?.error && (
            <p className="form-error">{formStatus.error}</p>
          )) ||
          (formStatus?.processing && (
            <p className="form-info">{formStatus.processing}</p>
          ))}
        <Button
          onClick={() => {
            handleSubmit(submit)()
          }}
          disabled={formStatus && formStatus.processing ? true : undefined}
        >
          Submit Review
        </Button>
      </StyledFormActions>
    </FormStyle>
  )
}

export default FormReview
